import { useCallback, useEffect, useState } from "react";
import { FaPlusSquare } from "react-icons/fa";
import { CiEdit } from "react-icons/ci";
import { IoTrashOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";
import ConfirmModal from "@modals/ConfirmModal";
import ReleaseEditModal from "@modals/ReleaseEditModal";
import {
  formatReleaseDate,
  summarizeReleaseFeatures,
  type ReleaseHistoryItem,
} from "@/utils/releaseHistory";

type ReleaseHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  updateSpinner?: ((show: boolean) => void) | null;
};

export default function ReleaseHistoryModal({
  isOpen,
  onClose,
  updateSpinner = null,
}: ReleaseHistoryModalProps) {
  const [releases, setReleases] = useState<ReleaseHistoryItem[]>([]);
  const [editing, setEditing] = useState<ReleaseHistoryItem | null | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<ReleaseHistoryItem | null>(null);

  const loadReleases = useCallback(async () => {
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.getAsync("/Releases");
      if (resp?.ok && Array.isArray(resp.jsonData)) {
        setReleases(resp.jsonData as ReleaseHistoryItem[]);
        return;
      }
      toast.error(resp?.jsonData?.message || "Failed to load releases.");
      setReleases([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load releases.";
      toast.error(message);
      setReleases([]);
    } finally {
      updateSpinner?.(false);
    }
  }, [updateSpinner]);

  useEffect(() => {
    if (!isOpen) {
      setEditing(undefined);
      setPendingDelete(null);
      return;
    }
    void loadReleases();
  }, [isOpen, loadReleases]);

  if (!isOpen) return null;

  async function confirmDelete() {
    if (!pendingDelete?.id) {
      setPendingDelete(null);
      return;
    }
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.deleteAsync(`/Releases/${pendingDelete.id}`);
      if (resp?.ok || resp?.status === 204) {
        toast.success("Release deleted.");
        setPendingDelete(null);
        await loadReleases();
        return;
      }
      toast.error(resp?.jsonData?.message || "Failed to delete release.");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to delete release.";
      toast.error(message);
    } finally {
      updateSpinner?.(false);
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        title="Release History"
        onClose={onClose}
        className="releaseHistoryModal"
      >
        <div className="modalBody releaseHistoryModalBody">
          <div className="tableWrapper dimensionTable scrollable releaseHistoryTable">
            <table>
              <caption>
                <span className="title">Releases</span>
                <span className="captionRight">
                  <FaPlusSquare
                    className="actionIcon plusIcon"
                    title="Add release"
                    onClick={() => setEditing(null)}
                  />
                </span>
              </caption>
              <thead>
                <tr>
                  <th>Release date</th>
                  <th>Frontend</th>
                  <th>Backend</th>
                  <th>Features</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {releases.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="emptyStateCell">
                      No releases recorded yet.
                    </td>
                  </tr>
                ) : (
                  releases.map((r) => (
                    <tr key={r.id}>
                      <td>{formatReleaseDate(r.releaseDate)}</td>
                      <td>{r.frontendVersion || "—"}</td>
                      <td>{r.backendVersion || "—"}</td>
                      <td>{summarizeReleaseFeatures(r.changes)}</td>
                      <td className="actions">
                        <CiEdit
                          className="actionIcon editIcon"
                          title="Edit"
                          onClick={() => setEditing(r)}
                        />
                        <IoTrashOutline
                          className="actionIcon deleteIcon"
                          title="Delete"
                          onClick={() => setPendingDelete(r)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>

      <ReleaseEditModal
        isOpen={editing !== undefined}
        release={editing ?? null}
        onClose={() => setEditing(undefined)}
        onSaved={() => {
          void loadReleases();
        }}
        updateSpinner={updateSpinner}
      />

      <ConfirmModal
        isOpen={pendingDelete !== null}
        title="Delete Release"
        message={`Delete the release dated ${formatReleaseDate(pendingDelete?.releaseDate)}?`}
        dangerText="Its feature notes will be removed as well."
        confirmText="Yes, Delete Release"
        onConfirm={() => {
          void confirmDelete();
        }}
        onCancel={() => setPendingDelete(null)}
        zIndex={1300}
      />
    </>
  );
}
